import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import OtpInput from '../components/OtpInput';
import { ResetRole, requestPasswordReset, resetPassword, verifyResetOtp } from '../api/auth';
import { extractApiError } from '../api/legalServices';
import logoMark from '../assets/logo-mark.png';

type Step = 'email' | 'code' | 'password' | 'done';

interface LocationState {
  email?: string;
}

interface ForgotPasswordProps {
  /**
   * Which login this flow belongs to — sets the branding, the way back, and
   * the only kind of account it can reset (sent to the API as `role`).
   */
  portal: ResetRole;
}

const RESEND_SECONDS = 60;
const STEP_NUMBER: Record<Step, number> = { email: 1, code: 2, password: 3, done: 3 };

const errorMessage = (err: any, fallback: string) =>
  err?.code === 'ERR_NETWORK' ? 'Cannot reach the server. Check your connection and try again.' : extractApiError(err, fallback);

/**
 * "Forgot password?" for the admin console and the merchant portal:
 * email → 6-digit code sent by email → new password.
 */
const ForgotPassword: React.FC<ForgotPasswordProps> = ({ portal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginPath = portal === 'admin' ? '/admin/login' : '/agent/login';

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState((location.state as LocationState | null)?.email ?? '');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendIn]);

  const goTo = (next: Step) => {
    setError(null);
    setNotice(null);
    setStep(next);
  };

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setNotice(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim(), portal);
      setOtp('');
      setResendIn(RESEND_SECONDS);
      if (step === 'code') setNotice('A new code is on its way.');
      else goTo('code');
    } catch (err) {
      setError(errorMessage(err, 'Could not send the code. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const checkCode = async (code: string = otp) => {
    setError(null);
    setNotice(null);
    if (code.length !== 6) {
      setError('Enter the 6-digit code from the email.');
      return;
    }
    setSubmitting(true);
    try {
      await verifyResetOtp(email.trim(), code, portal);
      goTo('password');
    } catch (err) {
      setError(errorMessage(err, 'That code did not work. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const saveNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({ email: email.trim(), otp, password, confirmPassword, role: portal });
      goTo('done');
    } catch (err) {
      const msg = errorMessage(err, 'Could not reset your password. Try again.');
      // The code expired or ran out of tries meanwhile — start again from the code step.
      if (/code/i.test(msg)) {
        setOtp('');
        setStep('code');
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const header = {
    email: {
      icon: 'key',
      title: 'Forgot password?',
      text: "Enter the email you sign in with and we'll send you a 6-digit code to reset your password.",
    },
    code: { icon: 'mail', title: 'Check your email', text: null },
    password: {
      icon: 'lock',
      title: 'Set a new password',
      text: 'Choose a new password with at least 6 characters.',
    },
    done: {
      icon: 'check-circle',
      title: 'Password updated',
      text: 'Your password has been reset. Sign in with your new password.',
    },
  }[step];

  const passwordField = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    autoComplete: string
  ) => (
    <div className="mb-4">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2">
          <Icon name="lock" size={16} color="var(--g400)" />
        </span>
        <input
          id={id}
          type={showPassword ? 'text' : 'password'}
          autoComplete={autoComplete}
          className="form-input"
          style={{ paddingLeft: 38, paddingRight: 42 }}
          placeholder="At least 6 characters"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={submitting}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md flex items-center justify-center hover:bg-g50"
          style={{ width: 36, height: 36 }}
          tabIndex={-1}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          <Icon name={showPassword ? 'eye-off' : 'eye'} size={16} color="var(--g500)" />
        </button>
      </div>
    </div>
  );

  const done = step === 'done';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-canvas">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img
            src={logoMark}
            alt="Rentagade"
            className="rounded-full object-contain flex-shrink-0"
            style={{ width: 48, height: 48 }}
          />
          <div className="text-left">
            <div className="text-xl font-extrabold text-ink tracking-tight leading-tight">Rentagade</div>
            <div className="text-[11px] font-semibold uppercase text-g400" style={{ letterSpacing: '1.2px' }}>
              {portal === 'admin' ? 'Admin Console' : 'Merchant Portal'}
            </div>
          </div>
        </div>

        <div className="card p-6 sm:p-8 animate-fadeIn">
          {/* Progress: email → code → new password */}
          <div className="flex gap-1.5 mb-6" aria-label={`Step ${STEP_NUMBER[step]} of 3`}>
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="h-1 flex-1 rounded-full transition-colors"
                style={{
                  background: done ? 'var(--green)' : n <= STEP_NUMBER[step] ? 'var(--brand)' : 'var(--g100)',
                }}
              />
            ))}
          </div>

          <div
            className="flex items-center justify-center rounded-full mb-4"
            style={{
              width: 48,
              height: 48,
              background: done ? 'var(--green-bg)' : 'var(--brand-bg)',
            }}
          >
            <Icon name={header.icon} size={22} color={done ? 'var(--green)' : 'var(--brand)'} />
          </div>

          <h1 className="text-[22px] font-extrabold text-ink mb-1">{header.title}</h1>
          <p className="text-sm text-g500 mb-6">
            {step === 'code' ? (
              <>
                We sent a 6-digit code to <span className="font-semibold text-ink">{email.trim()}</span>. It
                expires in 10 minutes.
              </>
            ) : (
              header.text
            )}
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-[10px] px-4 py-3 text-sm font-medium flex items-start gap-2"
              style={{
                background: 'var(--red-bg)',
                color: 'var(--red)',
                border: '1px solid rgba(220,38,38,.12)',
              }}
            >
              <Icon name="x-circle" size={16} color="var(--red)" />
              <span>{error}</span>
            </div>
          )}
          {notice && !error && (
            <div
              role="status"
              className="mb-4 rounded-[10px] px-4 py-3 text-sm font-medium flex items-start gap-2"
              style={{
                background: 'var(--green-bg)',
                color: 'var(--green)',
                border: '1px solid rgba(5,150,105,.12)',
              }}
            >
              <Icon name="check-circle" size={16} color="var(--green)" />
              <span>{notice}</span>
            </div>
          )}

          {step === 'email' && (
            <form onSubmit={sendCode} noValidate>
              <div className="mb-6">
                <label className="form-label" htmlFor="reset-email">
                  Email address
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Icon name="mail" size={16} color="var(--g400)" />
                  </span>
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    className="form-input"
                    style={{ paddingLeft: 38 }}
                    placeholder={portal === 'admin' ? 'admin@rentagade.com' : 'you@example.com'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary w-full justify-center"
                style={{ paddingTop: 12, paddingBottom: 12 }}
              >
                {submitting ? 'Sending code...' : 'Send code'}
              </button>
            </form>
          )}

          {step === 'code' && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                checkCode();
              }}
              noValidate
            >
              <OtpInput
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  if (error) setError(null);
                }}
                onComplete={(v) => checkCode(v)}
                disabled={submitting}
                invalid={!!error}
                autoFocus
              />
              <button
                type="submit"
                disabled={submitting || otp.length !== 6}
                className="btn btn-primary w-full justify-center mt-6"
                style={{
                  paddingTop: 12,
                  paddingBottom: 12,
                  opacity: otp.length !== 6 && !submitting ? 0.55 : undefined,
                }}
              >
                {submitting ? 'Checking...' : 'Verify code'}
              </button>
              <div className="flex items-center justify-between flex-wrap gap-2 mt-4 text-sm">
                <span className="text-g500">
                  Didn't get it?{' '}
                  {resendIn > 0 ? (
                    <span className="text-g400">Resend in {resendIn}s</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => sendCode()}
                      disabled={submitting}
                      className="font-semibold text-brand hover:underline"
                    >
                      Resend code
                    </button>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setOtp('');
                    goTo('email');
                  }}
                  className="font-semibold text-g600 hover:underline"
                >
                  Change email
                </button>
              </div>
              <p className="text-xs text-g400 mt-4">Can't find it? Check your spam or junk folder.</p>
            </form>
          )}

          {step === 'password' && (
            <form onSubmit={saveNewPassword} noValidate>
              {passwordField('new-password', 'New password', password, setPassword, 'new-password')}
              {passwordField('confirm-password', 'Confirm new password', confirmPassword, setConfirmPassword, 'new-password')}
              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary w-full justify-center mt-2"
                style={{ paddingTop: 12, paddingBottom: 12 }}
              >
                {submitting ? 'Saving...' : 'Reset password'}
              </button>
            </form>
          )}

          {done && (
            <button
              type="button"
              onClick={() => navigate(loginPath, { replace: true, state: { email: email.trim() } })}
              className="btn btn-primary w-full justify-center"
              style={{ paddingTop: 12, paddingBottom: 12 }}
            >
              Back to sign in <Icon name="chevron-right" size={16} color="#fff" />
            </button>
          )}

          {!done && (
            <div className="text-center mt-6 pt-6 text-sm" style={{ borderTop: '1px solid var(--g100)' }}>
              <Link
                to={loginPath}
                state={{ email: email.trim() }}
                className="inline-flex items-center gap-1.5 font-semibold text-g600 hover:text-ink"
              >
                <Icon name="arrow-left" size={14} color="currentColor" /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
